<template>
  <f7-link
    v-if="cart && cart.length > 0 && isSmallScreen"
    class="toolbarDeskTop"
    tab-link
    href="/cart/"
    transition="f7-fade"
    :aria-label="$t('home.cart')"
  >
    <f7-icon ios="f7:cart" md="material:shopping_cart" color="none">
      <f7-badge
        bg-color="none"
        class="text-align-center"
        :style="cartBadgeStyle"
        text-color="red"
        >{{ totalItemCount }}</f7-badge
      >
    </f7-icon>
  </f7-link>

  <f7-link
    v-if="isSmallScreen && keycloakStore.authenticated"
    @click="scanQRCode()"
    icon-ios="f7:qrcode_viewfinder"
    icon-md="material:qr_code_scanner"
    :aria-label="$t('common.scan')"
  ></f7-link>

  <f7-link
    v-if="keycloakStore.authenticated"
    href="/notification/"
    class="toolbarDeskTop"
    :aria-label="$t('common.notification')"
  >
    <f7-icon ios="f7:bell_fill" md="material:notifications" />
  </f7-link>
  <f7-link
    v-if="keycloakStore.authenticated"
    class="toolbarDeskTop"
    raised
    panel-open=".loginPanel"
    :aria-label="$t('common.account')"
  >
    <img
      v-if="userAccount && userAccount.avatar !== null"
      :src="userAccount.avatar"
      :style="compactAvatarStyle"
      :alt="$t('common.a11y.userAvatar')"
    />
    <f7-badge
      v-else
      slot="media"
      color="auto"
      :style="compactAvatarStyle"
      >{{ $filtersGetInitial.getInitial(displayName) }}</f7-badge
    >
  </f7-link>

  <f7-link
  v-else
    icon-ios="f7:square_arrow_right"
    icon-md="material:login" 
    icon-color="primary"
    class="Login_or_Register"
    :aria-label="$t('common.login')"
    @click="openLogingPrompt()"
  ></f7-link>

  <f7-sheet
    class="Login_or_Register_Sheet"
    style="height: auto; --f7-sheet-bg-color: #fff"
    swipe-to-close
    backdrop
    top
  >
    <f7-page-content>
      <f7-block>
        <f7-list dividers-ios strong-ios inset>
          <f7-list-item
            :title="$t('common.login')"
            link="#"
            sheet-close
            @click="loginFnMethod()"
            text-color="primary"
            class="margin-bottom"
            style="font-weight: bold"
            ><template #media>
              <f7-icon ios="f7:square_arrow_right" md="material:login"></f7-icon>
            </template>
          </f7-list-item>
          <f7-list-item
            :title="$t('common.register')"
            link="#"
            text-color="primary"
            sheet-close
            @click="registerFn()"
            class="margin-bottom"
            style="font-weight: bold"
            ><template #media>
              <f7-icon ios="f7:person_badge_plus_fill" md="material:person_add_alt">
              </f7-icon>
            </template>
          </f7-list-item>
          <f7-list-item :title="$t('common.close')" text-color="gray" link="#" sheet-close
            ><template #media>
              <f7-icon ios="f7:xmark" md="material:close"></f7-icon>
            </template>
          </f7-list-item>
        </f7-list>
      </f7-block>
    </f7-page-content>
  </f7-sheet>
</template>
<script setup>
import { computed, onMounted, ref } from "vue";
import { f7 } from "framework7-vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { useLogin } from "@/js/composables/useLogin.js";
import { useScanQRCode } from "@/js/composables/useScanQRCode.js";
import { useAuthStore } from "../js/pinia-stores/auth.js";
import { useAccountStore } from "../js/pinia-stores/account.js";
import { useShoppingCartStore } from "../js/pinia-stores/shoppingCart.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import {
  useSmallScreen,
  useTabletScreen,
} from "../js/composables/useMediaQuery.js";

const { t } = useI18n();

const { loginFn: loginFnMethod, registerFn } = useLogin();
const { scanQRCode } = useScanQRCode();

const keycloakStore = useKeycloakStore();

const authStore = useAuthStore();
const { profile } = storeToRefs(authStore);
const { userAccount } = storeToRefs(useAccountStore());
const { cart, totalItemCount } = storeToRefs(useShoppingCartStore());

const isSmallScreen = useSmallScreen().matches;
const isTablet = useTabletScreen().matches;

const actionsToLogin = ref(null);

const displayName = computed(() =>
  userAccount.value ? userAccount.value.name : profile.value?.name
);

function avatarStyle(size) {
  return {
    height: size,
    width: size,
    "min-width": size,
    "background-size": "cover",
    display: "block",
    "border-radius": "50%",
  };
}

const compactAvatarStyle = computed(() => avatarStyle("24px"));

const cartBadgeStyle = computed(() => ({
  "font-size": "50%",
  "margin-left": totalItemCount.value > 9 ? "-23px" : "-20px",
  top: "-5px",
  "font-weight": "bold",
}));

onMounted(() => {
  if (!userAccount.value && keycloakStore.authenticated) {
    useAccountStore().loadAccount(keycloakStore.subject);
  }
});

function openLogingPrompt() {
  if (isTablet.value) {
    openLoginActions();
  } else {
    f7.sheet.open(".Login_or_Register_Sheet");
  }
}

function openLoginActions() {
  if (!actionsToLogin.value) {
    actionsToLogin.value = f7.actions.create({
      backdrop: true,
      animate: true,
      closeByOutsideClick: true,
      convertToPopover: true,
      buttons: [
        {
          text: t("common.login"),
          color: "red",
          bold: true,
          icon: `<i class="f7-icons color-red if-not-md">square_arrow_right</i><i class="material-icons md-only color-primary">login</i>
              `,
          onClick() {
            loginFnMethod();
          },
        },
        {
          text: t("common.register"),
          color: "auto",
          bold: true,
          icon: `<i class="f7-icons if-not-md color-auto">person_badge_plus_fill</i><i class="material-icons md-only color-auto">person_add_alt</i>
              `,
          onClick() {
            registerFn();
          },
        },
        {
          text: t("common.close"),
          color: "gray",
          icon: `<i class="f7-icons if-not-md color-gray">xmark</i><i class="material-icons md-only color-gray">close</i>
              `,
        },
      ],
      targetEl: f7.$(".Login_or_Register"),
    });
  }
  actionsToLogin.value.open();
}
</script>